/**
 * detectVellaContent
 * ------------------
 * Inspects an AI response (plain text) and decides whether it carries
 * generated content that deserves a rich preview card. Returns one of:
 *
 *   { kind: "website", html, title?, url?, leadText?, domain? }
 *   { kind: "social",  platform, postType, brand, asset, caption, hashtags, leadText? }
 *   { kind: "ad",      adType, brand, headline, description, cta, asset, targeting, estimatedReach, sitelinks?, leadText? }
 *   null                 // nothing to upgrade — render normal text bubble
 *
 * Detection prefers an explicit `vellaContent` JSON envelope embedded in
 * the response, e.g.:
 *
 *   ```vella
 *   { "kind": "social", "platform": "Instagram", ... }
 *   ```
 *
 * As a fallback it scans for fenced HTML blocks (website), and JSON
 * objects that look like social posts or ad campaigns.
 *
 * The detector is intentionally permissive — false negatives are fine
 * (we just show a normal bubble), false positives must be avoided.
 */

const VELLA_FENCE_RE = /```(?:vella|vella-content|vellacontent|vellapreview)\s*\n([\s\S]*?)\n```/i;
const JSON_FENCE_RE  = /```json\s*\n([\s\S]*?)\n```/i;
const HTML_FENCE_RE  = /```(?:html|htm)\s*\n([\s\S]*?)\n```/i;
const RAW_HTML_RE    = /<!doctype\s+html[\s\S]*<\/html>/i;

const SOCIAL_PLATFORMS = ["instagram", "facebook", "tiktok", "linkedin"];
const AD_TYPES = ["GoogleSearch", "GoogleDisplay", "MetaFeed", "InstagramStory", "TikTokInFeed"];

export function detectVellaContent(text, attached) {
  if (!text || typeof text !== "string") return null;

  // 1) Explicit envelope wins.
  const envelope = parseEnvelope(text);
  if (envelope) return normalize(envelope, text);

  // 2) Out-of-band attached content (caller passed `vellaContent` on the message).
  if (attached && typeof attached === "object") {
    const normalized = normalize(attached, text);
    if (normalized) return normalized;
  }

  // 3) Raw HTML document → website.
  const htmlFenced = text.match(HTML_FENCE_RE);
  if (htmlFenced && looksLikeFullHtml(htmlFenced[1])) {
    return {
      kind: "website",
      html: htmlFenced[1].trim(),
      leadText: leadFromText(text, htmlFenced.index),
    };
  }
  const rawHtml = text.match(RAW_HTML_RE);
  if (rawHtml) {
    return {
      kind: "website",
      html: rawHtml[0].trim(),
      leadText: leadFromText(text, rawHtml.index),
    };
  }

  // 4) Free-floating JSON that looks like a social post or ad.
  const jsonFenced = text.match(JSON_FENCE_RE);
  if (jsonFenced) {
    const parsed = safeJson(jsonFenced[1]);
    if (parsed) {
      const guess = classifyJson(parsed);
      if (guess) return normalize(guess, text, jsonFenced.index);
    }
  }

  return null;
}

// ─── helpers ───────────────────────────────────────────

function parseEnvelope(text) {
  const m = text.match(VELLA_FENCE_RE);
  if (!m) return null;
  const parsed = safeJson(m[1]);
  if (!parsed) return null;
  return parsed;
}

function safeJson(raw) {
  if (!raw) return null;
  try {
    return JSON.parse(raw.trim());
  } catch {
    // Try to extract the first balanced { ... } block — LLMs sometimes
    // tack on prose around the JSON they were asked for.
    const start = raw.indexOf("{");
    const end = raw.lastIndexOf("}");
    if (start === -1 || end === -1 || end < start) return null;
    try { return JSON.parse(raw.slice(start, end + 1)); } catch { return null; }
  }
}

function looksLikeFullHtml(s) {
  if (!s) return false;
  const lower = s.toLowerCase();
  return lower.includes("<html") || lower.includes("<!doctype") || (lower.includes("<body") && lower.length > 200);
}

function classifyJson(obj) {
  if (!obj || typeof obj !== "object") return null;
  if (obj.kind) return obj;

  const hasPlatform = typeof obj.platform === "string" && SOCIAL_PLATFORMS.includes(obj.platform.toLowerCase());
  const hasCaption = typeof obj.caption === "string";
  if (hasPlatform && hasCaption) return { ...obj, kind: "social" };

  const adTypeKey = typeof obj.adType === "string" ? obj.adType : null;
  const looksAd = (adTypeKey && AD_TYPES.includes(adTypeKey))
    || (typeof obj.headline === "string" && (typeof obj.description === "string" || typeof obj.cta === "string"))
    || obj.targeting;
  if (looksAd) return { ...obj, kind: "ad" };

  if (typeof obj.html === "string" && looksLikeFullHtml(obj.html)) return { ...obj, kind: "website" };

  return null;
}

function normalize(obj, text, fenceIndex) {
  if (!obj || !obj.kind) return null;
  const kind = String(obj.kind).toLowerCase();
  const leadText = obj.leadText || (typeof fenceIndex === "number" ? leadFromText(text, fenceIndex) : leadFromText(text, text.length));

  if (kind === "website") {
    if (!obj.html && !obj.url) return null;
    return {
      kind: "website",
      html: obj.html || "",
      url: obj.url || obj.previewUrl,
      title: obj.title || obj.name,
      domain: obj.domain,
      leadText,
    };
  }
  if (kind === "social") {
    if (!obj.platform) return null;
    return {
      kind: "social",
      platform: capitalize(obj.platform),
      postType: obj.postType || obj.type || "Feed",
      brand: obj.brand || {},
      asset: obj.asset || (obj.imageUrl ? { url: obj.imageUrl, type: "image" } : obj.videoUrl ? { url: obj.videoUrl, type: "video" } : {}),
      caption: obj.caption || obj.text || "",
      hashtags: Array.isArray(obj.hashtags) ? obj.hashtags : [],
      leadText,
    };
  }
  if (kind === "ad") {
    return {
      kind: "ad",
      adType: obj.adType || "MetaFeed",
      brand: obj.brand || {},
      headline: obj.headline || obj.title || "",
      description: obj.description || obj.body || "",
      cta: obj.cta || obj.ctaText || "Learn More",
      asset: obj.asset || (obj.imageUrl ? { url: obj.imageUrl, type: "image" } : obj.videoUrl ? { url: obj.videoUrl, type: "video" } : {}),
      sitelinks: Array.isArray(obj.sitelinks) ? obj.sitelinks : undefined,
      targeting: obj.targeting,
      estimatedReach: obj.estimatedReach ?? obj.reach,
      displaySize: obj.displaySize,
      leadText,
    };
  }
  return null;
}

function leadFromText(text, beforeIndex) {
  if (!text) return "";
  const slice = text.slice(0, beforeIndex == null ? text.length : beforeIndex);
  // Strip any trailing fence intro like "Here's the site I built for you:".
  const trimmed = slice.replace(/```[\s\S]*$/, "").trim();
  if (!trimmed) return "";
  // Keep it short — chat lead, not full prose.
  if (trimmed.length <= 280) return trimmed;
  const cut = trimmed.lastIndexOf(".", 280);
  return (cut > 80 ? trimmed.slice(0, cut + 1) : trimmed.slice(0, 280)) + "…";
}

function capitalize(s) {
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}
